/**
 * BuyingLanding.tsx — the sealed `/buying` micro-site.
 *
 * Purple lane only. Nav omits `/building` and any cross-sell. Pricing view
 * shows the $35–60K TPRM add-on band only. `?from=procurement` param
 * stamps a "Prepared for procurement review" banner so Reyna's forwarding
 * workflow feels intentional.
 *
 * Public, no auth. See docs/v4/launch-scope.md P0-1 and docs/v4/link-gaps-plan.md GAP-3.
 */

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Brand, Button, marketingCopy } from '@aigovops/design-system';

const { gtmV4 } = marketingCopy;

const laneAccent = 'var(--buying)';
const laneAccentSoft = 'var(--buying-soft)';

export function BuyingLanding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const fromProcurement = params.get('from') === 'procurement';
  const lane = gtmV4.lanes.buying;

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100dvh' }}>
      {/* Sealed header — no `/building` link, no cross-sell. */}
      <header
        style={{
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            maxWidth: '80rem',
            margin: '0 auto',
            padding: 'var(--space-4) var(--space-6)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
          }}
        >
          <Link
            to="/buying"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              textDecoration: 'none',
              minWidth: 0,
            }}
          >
            <Brand variant="glyph" />
            <Brand variant="wordmark" />
            <span
              style={{
                marginLeft: 'var(--space-2)',
                padding: '2px var(--space-2)',
                border: `1px solid ${laneAccent}`,
                borderRadius: '999px',
                color: laneAccent,
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--font-size-3xs)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 700,
              }}
            >
              Buying
            </span>
          </Link>
          <nav
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 'var(--space-4)',
              fontSize: 'var(--font-size-2xs)',
              fontFamily: 'var(--font-ui)',
            }}
          >
            <a href="#how" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
              How it works
            </a>
            <a href="#pricing" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
              Pricing
            </a>
            <Link to="/buying/samples/supplier-ai-register" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
              Sample register
            </Link>
            <Button
              variant="primary"
              compact
              onClick={() =>
                window.open(
                  '/demo',
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              Book a demo
            </Button>
          </nav>
        </div>
      </header>

      {/* Procurement banner if arrived via ?from=procurement */}
      {fromProcurement ? (
        <div
          role="note"
          style={{
            background: laneAccentSoft,
            borderBottom: `1px solid ${laneAccent}`,
            padding: 'var(--space-3) var(--space-6)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: laneAccent,
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          PREPARED FOR PROCUREMENT REVIEW — TPRM ADD-ON MOTION · Pricing band and demo scope on this page apply
        </div>
      ) : null}

      {/* Hero */}
      <section
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-5)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: laneAccent,
            fontWeight: 700,
          }}
        >
          {lane.microLabel}
        </div>
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
            color: 'var(--text)',
            maxWidth: '48rem',
          }}
        >
          {lane.heading}
        </h1>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-2)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: 700,
              lineHeight: 1,
              color: 'var(--text)',
              letterSpacing: '-0.02em',
            }}
          >
            {lane.numericAtom}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-md)',
              color: 'var(--text-muted)',
            }}
          >
            {lane.numericCaption}
          </span>
        </div>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-md)',
            color: 'var(--text-muted)',
            lineHeight: 'var(--line-height-body)',
            maxWidth: '52rem',
          }}
        >
          {gtmV4.whichRoom.motions[0].body}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
          <Button onClick={() => navigate('/buying/samples/supplier-ai-register')}>
            {lane.ctaLabel} →
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              window.open(
                '/demo',
                '_blank',
                'noopener,noreferrer',
              )
            }
          >
            Book a 20-min walkthrough
          </Button>
        </div>
      </section>

      {/* How it works — 4 steps: attest → subscribe → delta → escalate */}
      <section
        id="how"
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-5)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          How the continuous register works
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
            gap: 'var(--space-4)',
          }}
        >
          {STEPS.map((step, i) => (
            <article
              key={step.title}
              style={{
                padding: 'var(--space-5)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)',
                background: 'var(--surface)',
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  color: laneAccent,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                }}
              >
                STEP {String(i + 1).padStart(2, '0')}
              </div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-md)',
                  fontWeight: 600,
                  color: 'var(--text)',
                }}
              >
                {step.title}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  lineHeight: 'var(--line-height-body)',
                }}
              >
                {step.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Pricing rail — TPRM band only */}
      <section
        id="pricing"
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-5)',
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontFamily: 'var(--font-ui)',
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: 700,
              color: 'var(--text)',
            }}
          >
            TPRM add-on pricing
          </h2>
          <p
            style={{
              margin: 'var(--space-2) 0 0',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              maxWidth: '52rem',
            }}
          >
            Departmental buy under existing TPRM budget authority. Signed as a scoped add-on to
            your third-party risk program — not a platform decision.
          </p>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
            gap: 'var(--space-4)',
          }}
        >
          {TPRM_TIERS.map((tier) => (
            <article
              key={tier.name}
              style={{
                padding: 'var(--space-5)',
                border: tier.emphasized ? `1px solid ${laneAccent}` : '1px solid var(--border)',
                borderRadius: 'var(--radius-card)',
                background: 'var(--surface)',
                display: 'grid',
                gap: 'var(--space-3)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: tier.emphasized ? laneAccent : 'var(--text-dim)',
                  fontWeight: 700,
                }}
              >
                {tier.eyebrow}
              </div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 700,
                  color: 'var(--text)',
                }}
              >
                {tier.name}
              </h3>
              <div
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'clamp(1.5rem, 2.5vw, 1.875rem)',
                  fontWeight: 700,
                  color: 'var(--text)',
                }}
              >
                {tier.price}
                <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)', fontWeight: 500, marginLeft: '4px' }}>
                  {tier.cadence}
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  lineHeight: 'var(--line-height-body)',
                }}
              >
                {tier.description}
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 'var(--space-4)',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text)',
                  display: 'grid',
                  gap: 'var(--space-1)',
                }}
              >
                {tier.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <div style={{ marginTop: 'var(--space-2)' }}>
                <Button
                  variant={tier.emphasized ? 'primary' : 'secondary'}
                  onClick={() =>
                    window.open(
                      '/demo',
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  Book a walkthrough
                </Button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Bottom CTA — matches 104px section rhythm; no divider (rhythm is enough). */}
      <section
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-3)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          See the register before you commit.
        </h2>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            maxWidth: '52rem',
          }}
        >
          A 20-minute walkthrough wired against your own supplier list — or the public sample
          register if procurement wants a first look.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
          <Button
            variant="primary"
            onClick={() =>
              window.open(
                '/demo',
                '_blank',
                'noopener,noreferrer',
              )
            }
          >
            Book a 20-min walkthrough →
          </Button>
          <Link
            to="/buying/samples/supplier-ai-register"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--space-2) var(--space-3)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-sm)',
              color: laneAccent,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              fontWeight: 600,
            }}
          >
            See the sample supplier AI register →
          </Link>
        </div>
      </section>

      {/* Footer — no cross-sell to /building or `/`. */}
      <footer
        style={{
          borderTop: '1px solid var(--border)',
          padding: 'var(--space-6)',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            maxWidth: '80rem',
            margin: '0 auto',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <span>© 2026 gacpro · gacpro.io · hello@gacpro.io</span>
          <span>
            <Link to="/buying/samples/supplier-ai-register" style={{ color: 'var(--text-dim)', marginRight: 'var(--space-3)' }}>
              Sample register
            </Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

const STEPS = [
  {
    title: 'Suppliers attest',
    body: 'Each supplier signs their own AI system attestations into their register — data-boundary declarations, framework mappings, named signer.',
  },
  {
    title: 'You subscribe',
    body: 'Add suppliers to your portfolio. Their current attestation renders on your dashboard, scored against your risk bar. No survey to send.',
  },
  {
    title: 'Delta alerts',
    body: "When a supplier's register changes — new AI system, revised data-boundary, expired attestation — your subscribed team gets notified.",
  },
  {
    title: 'Escalate on SLA',
    body: 'Suppliers that fall outside your oversight scope trigger a documented remediation workflow — owner assignment, review SLA clock, board-committee escalation path.',
  },
];

const TPRM_TIERS = [
  {
    eyebrow: 'Wedge',
    name: 'AI Supplier Disclosure',
    price: '$35K',
    cadence: '/year',
    description: 'For a defined supplier portfolio — up to 60 vendors, one team, no cross-org rollup.',
    features: [
      'Continuous supplier register',
      'SIG 2026 AI section imports',
      'Delta alerts + Q3 board rollup',
      'Layer 2 signature model',
    ],
    emphasized: false,
  },
  {
    eyebrow: 'Portfolio bundle',
    name: 'Enterprise TPRM',
    price: '$60K',
    cadence: '/year',
    description: 'Up to 300 vendors, multiple teams, cross-org rollup, identity-log correlation for undisclosed-AI detection.',
    features: [
      'Everything in Wedge',
      'Undisclosed-AI correlation (Okta / Entra)',
      'Named executive sign-off routing',
      'Audit-committee-ready quarterly report',
    ],
    emphasized: true,
  },
];
